/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from "react";
import { IconX } from "@tabler/icons-react";
import FileUploader from "../../../components/common/FileUploader";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import toast from "react-hot-toast";
import {
  useItemBulkDocMutation,
  useFetchItemTemplate,
  useFetchProductItemData,
} from "../../../apis/productApis";
import {
  styleAndProtectDummyRow,
  cleanUploadedExcelFile,
} from "../../../utilities/bulkUploadHelper";
import { useLanguage } from "../../../context/LanguageContext";

type ItemBulkUploadModalsProps = {
  onClose: () => void;
  onResponseOpen: () => void;
  setItemBulkResponse: React.Dispatch<React.SetStateAction<any>>;
  refetchTable: () => void;
};

export default function ItemBulkUploadModals({
  onClose,
  onResponseOpen,
  setItemBulkResponse,
  refetchTable,
}: ItemBulkUploadModalsProps) {
  const { t } = useLanguage();
  const [file, setFile] = useState<File | null>(null);

  const { mutate: uploadDocMutate, isPending: isBulkUploading } =
    useItemBulkDocMutation();
  const { data: templateData, refetch: fetchTemplates } =
    useFetchItemTemplate();

  const handleUploadDoc = async () => {
    if (!file) {
      toast.error(t("Please select a file to upload"));
      return;
    }
    const cleanedFile = await cleanUploadedExcelFile(file, templateData?.[0]);
    uploadDocMutate(
      { item: cleanedFile },
      {
        onSuccess: (data: any) => {
          setItemBulkResponse(data.data);
          onClose();
          refetchTable();
          toast.success(t("File uploaded successfully, processing..."));
        },
        onError: (error: any) => {
          console.log(error, "error");
          toast.error(t(String(error)));
        },
      }
    );
  };

  const handleDownloadTemplate = async () => {
    if (!templateData || templateData.length === 0) {
      toast.error(t("Template structure is not loaded yet"));
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Item Template");

    // Row 1: Info
    const infoRow = worksheet.addRow([
      "This template is for importing item/product data. Fields in red are mandatory; others are optional. Please do not modify the first 4 rows (Row 4 is sample/dummy data).",
    ]);
    worksheet.mergeCells(`A1:AD1`);
    infoRow.eachCell((cell: any) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "E2EFD9" },
      };
      cell.font = {
        name: "Arial",
        size: 10,
        bold: true,
        color: { argb: "212121" },
      };
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
      cell.border = {
        top: { style: "thin", color: { argb: "FF000000" } },
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
    });

    infoRow.height = 25;

    // Row 2: Validation row
    const validations = [
      "Item Name is required",
      "SKU is required",
      "Category name is required",
      "Sub Category name is required",
      "Commodity is required",
      "Import Commodity is required",
      "Dangerous Goods (YES / NO) is required",
      "HS Code is required",
      "Storage Type (PALLETS / BINS) is required",
      "Capacity is required",
      "Minimum Quantity is required",
      "Length is required",
      "Width is required",
      "Height is required",
      "Weight is required",
      "Price 1 is required",
      "Price 2 is required",
      "Discount is optional",
      "Minimum Order is required",
      "Short Description is required",
      "Long Description is required",
      "Product Tags (comma-separated) is optional",
      "Variant SKU is optional",
      "Variant Name (e.g. Size/Color) is optional",
      "Variant Value (e.g. 60 Tablets/Black) is optional",
      "Variant Price is optional",
      "Expiry (YES / NO) is required",
      "Expiry Date (YYYY-MM-DD) is optional",
      "Terms and Conditions is optional",
      "Publish on App (YES / NO) is required",
    ];

    const validationRow = worksheet.addRow(validations);
    validationRow.eachCell((cell: any) => {
      const colIndex = Number(cell.col);
      // Red for required, yellow for optional
      const isRequired = ![18, 22, 23, 24, 25, 26, 28, 29].includes(colIndex);
      const fillColor = isRequired ? "FFCCCC" : "FFEB9C";

      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: fillColor },
      };

      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };

      cell.font = { color: { argb: "212121" }, size: 9 };

      cell.border = {
        top: { style: "thin", color: { argb: "FF000000" } },
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
    });
    validationRow.height = 45;

    // Row 3: Headings
    const headings = Object.keys(templateData[0] || {});
    const headingsRow = worksheet.addRow(headings);

    headingsRow.eachCell((cell: any) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "217447" },
      };
      cell.font = { name: "Arial", bold: true, color: { argb: "ffffff" } };
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
      cell.border = {
        top: { style: "thin", color: { argb: "FF000000" } },
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
    });
    headingsRow.height = 25;

    // Row 4: Dummy Data Row
    let dummyRow: ExcelJS.Row | undefined;
    if (templateData && templateData.length > 0 && templateData[0]) {
      const dummyValues = Object.values(templateData[0]);
      dummyRow = worksheet.addRow(dummyValues);
      dummyRow.eachCell((cell: any) => {
        cell.font = { name: "Arial", size: 10, color: { argb: "212121" } };
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        };
      });
      dummyRow.height = 20;
    }

    // Adjust column widths
    worksheet.columns = headings.map(() => ({ width: 22 }));

    await styleAndProtectDummyRow(worksheet, dummyRow);

    // Export
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "items-template.xlsx");
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  return (
    <div className="relative bg-white rounded-3xl shadow-sm border flex flex-col overflow-hidden h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <h2 className="text-xl font-semibold text-[#212121]">
          {t("Bulk Upload Items")}
        </h2>

        <div className="flex items-center gap-3">
          <button
            onClick={handleUploadDoc}
            disabled={isBulkUploading}
            className="px-4 py-2 bg-[#FBD037] hover:bg-[#ebd037]/90 transition-colors text-[#212121] rounded-lg font-medium"
          >
            {isBulkUploading ? t("Uploading...") : t("Submit")}
          </button>

          <button
            onClick={onClose}
            className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-50 transition-colors"
          >
            <IconX size={20} />
          </button>
        </div>
      </div>

      <div className="space-y-4 p-6 overflow-y-auto">
        <FileUploader
          name="item"
          img="/img/upload-img.png"
          title={t("Upload Excel File")}
          message={t("Upload a CSV or Excel file only (Max size: 2 MB).")}
          classNameMain="bg-transparent border-none flex items-center gap-3"
          fileVal={file}
          setFileVal={(file: any) => {
            setFile(file);
          }}
          showPreview={true}
          required
        />

        {file && (
          <p className="text-sm text-green-600 font-medium bg-green-50 px-3 py-2 rounded-md border border-green-200">
            {t("Selected File: ")}{file.name}
          </p>
        )}

        <ul className="list-disc px-5 text-sm text-[#1E293B] font-medium space-y-1">
          <li>{t("Download the Excel template before uploading.")}</li>
          <li>{t("Do not change column headers or structure.")}</li>
          <li>{t("All fields are required except for Discount and Product Tags.")}</li>
          <li>{t("Ensure Category and Sub Category names exist in your database.")}</li>
        </ul>

        <div className="flex flex-wrap items-center justify-between gap-4 bg-[#fff] px-4 py-2 rounded-md border border-[#D2D2D2]">
          <p className="text-xs text-[#212121] font-medium">
            <b>{t("Note")}</b> {t(": Please upload the file in the same format as the downloaded template file.")}
          </p>
          <button
            onClick={handleDownloadTemplate}
            className="bg-[#000000] text-white flex items-center space-x-2 px-4 py-2.5 rounded-md text-sm font-semibold whitespace-nowrap hover:bg-black/90 transition-colors"
          >
            <img src="/img/csvxl.svg" alt="" />
            <span>{t("Download Template")}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
